import React, { useState, useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaUser, FaSave, FaArrowLeft, FaCamera } from 'react-icons/fa';
import { showSuccessToast, showErrorToast } from '../../../shared/utils/toastHelper';
import apiClient from '../../../shared/utils/apiConfig';

const DoctorProfileEdit = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const [profileData, setProfileData] = useState({
        name: '',
        description_ar: '',
        description_en: '',
        image: '',
        phone: ''
    });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setTitle('تعديل الملف الشخصي');
        fetchProfileData();
    }, [setTitle]);

    const fetchProfileData = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get('/doctor/profile_data');
            
            if (response.data && response.data.status === 'success' && response.data.result) {
                const doctorData = response.data.result;
                
                // تعيين البيانات بالهيكل الصحيح
                setProfileData({
                    name: doctorData.name || '',
                    description_ar: doctorData.description || '', // الوصف يأتي في حقل description
                    description_en: doctorData.description_en || '',
                    image: doctorData.image || '',
                    phone: doctorData.phone || ''
                });
            } else {
                throw new Error(response.data?.message || 'فشل في جلب البيانات');
            }
        } catch (error) {
            showErrorToast(error.response?.data?.message || error.message || 'فشل في جلب البيانات');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setProfileData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setProfileData(prev => ({
                    ...prev,
                    image: e.target.result
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!profileData.name.trim()) {
            showErrorToast('يرجى إدخال الاسم');
            return;
        }

        try {
            setSaving(true);
            
            const formData = new FormData();
            formData.append('name', profileData.name);
            formData.append('description', profileData.description_ar || ''); // استخدام description بدلاً من description_ar
            formData.append('description_ar', profileData.description_ar || '');
            formData.append('description_en', profileData.description_en || '');
            formData.append('phone', profileData.phone || '');
            
            if (profileData.image && profileData.image.startsWith('data:')) {
                // إذا كانت صورة جديدة (base64)
                const response = await fetch(profileData.image);
                const blob = await response.blob();
                formData.append('image', blob, 'profile.jpg');
            }

            const response = await apiClient.post('/doctor/update_profile_data', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.status === 'success' || response.data.statusCode === 200) {
                showSuccessToast('تم حفظ البيانات بنجاح');
                navigate('/doctor/profile');
            } else {
                throw new Error(response.data.message || 'فشل في حفظ البيانات');
            }
        } catch (error) {
            showErrorToast(error.response?.data?.message || error.message || 'فشل في حفظ البيانات');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">جاري تحميل البيانات...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="d-flex align-items-center">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="btn btn-outline-secondary mx-3"
                            onClick={() => navigate('/doctor/profile')}>
                            <FaArrowLeft className="mx-2" />
                            العودة
                        </motion.button>
                        <h4 className="mb-0">تعديل الملف الشخصي</h4>
                    </div>
                </div>

                {/* Form */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="card shadow-lg border-0">
                    <div className="card-body p-4">
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                {/* Profile Image */}
                                <div className="col-md-4 text-center mb-4">
                                    <div className="position-relative d-inline-block">
                                        <div className="profile-image-container">
                                            {profileData.image ? (
                                                <img
                                                    src={profileData.image}
                                                    alt="Profile"
                                                    className="profile-image"
                                                />
                                            ) : (
                                                <div className="profile-placeholder">
                                                    <FaUser size={60} />
                                                </div>
                                            )}
                                        </div>
                                        <label className="image-upload-btn" title="تغيير الصورة">
                                            <FaCamera />
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageChange}
                                                style={{ display: 'none' }}
                                            />
                                        </label>
                                    </div>
                                    <p className="text-muted mt-2">انقر على الكاميرا لتغيير الصورة</p>
                                </div>

                                {/* Form Fields */}
                                <div className="col-md-8">
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">
                                                الاسم <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="name"
                                                value={profileData.name || ''}
                                                onChange={handleInputChange}
                                                placeholder="أدخل الاسم"
                                                required
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">رقم الهاتف</label>
                                            <input
                                                type="tel"
                                                className="form-control"
                                                name="phone"
                                                value={profileData.phone || ''}
                                                onChange={handleInputChange}
                                                placeholder="أدخل رقم الهاتف"
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">الوصف بالعربية</label>
                                            <textarea
                                                className="form-control"
                                                name="description_ar"
                                                value={profileData.description_ar || ''}
                                                onChange={handleInputChange}
                                                rows="3"
                                                placeholder="أدخل الوصف بالعربية"
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">الوصف بالإنجليزية</label>
                                            <textarea
                                                className="form-control"
                                                name="description_en"
                                                value={profileData.description_en || ''}
                                                onChange={handleInputChange}
                                                rows="3"
                                                placeholder="أدخل الوصف بالإنجليزية"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <div className="d-flex justify-content-end gap-3 mt-4">
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => navigate('/doctor/profile')}>
                                    إلغاء
                                </motion.button>
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    type="submit"
                                    className="btn text-white d-flex align-items-center"
                                    style={{ backgroundColor: '#184194' }}
                                    disabled={saving}>
                                    {saving ? (
                                        <>
                                            <div className="spinner-border spinner-border-sm mx-2" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            جاري الحفظ...
                                        </>
                                    ) : (
                                        <>
                                            <FaSave className="mx-2" />
                                            حفظ التغييرات
                                        </>
                                    )}
                                </motion.button>
                            </div>
                        </form>
                    </div>
                </motion.div>
            </div>

            {/* CSS */}
            <style jsx>{`
                .profile-image-container {
                    width: 150px;
                    height: 150px;
                    border-radius: 50%;
                    overflow: hidden;
                    border: 4px solid #e9ecef;
                    margin: 0 auto;
                }

                .profile-image {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .profile-placeholder {
                    width: 100%;
                    height: 100%;
                    background: #f8f9fa;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #6c757d;
                }

                .image-upload-btn {
                    position: absolute;
                    bottom: 0;
                    right: 0;
                    background: #184194;
                    color: white;
                    border: none;
                    border-radius: 50%;
                    width: 40px;
                    height: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .image-upload-btn:hover {
                    background: #0d2a5c;
                    transform: scale(1.1);
                }
            `}</style>
        </div>
    );
};

export default DoctorProfileEdit;
